// /home/labdoo/zitech-nexus/frontend/src/pages/admin/AdminBlogCommentsList.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { blogAPI } from '../../services/api';
import './AdminBlogManagement.css';

// SVG Icons
const Icons = {
  Approve: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
      <path d="M20 6L9 17L4 12" />
    </svg>
  ),
  Delete: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7H20" />
      <path d="M10 11V16" />
      <path d="M14 11V16" />
      <path d="M5 7L6 19C6 19.5304 6.21071 20.0391 6.58579 20.4142C6.96086 20.7893 7.46957 21 8 21H16C16.5304 21 17.0391 20.7893 17.4142 20.4142C17.7893 20.0391 18 19.5304 18 19L19 7" />
      <path d="M9 7L10 3H14L15 7" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="abm-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7443C15.6251 19.5894 14.0782 20.0573 12.5 20.1C11.1801 20.1034 9.87812 19.7951 8.7 19.2L3 21L4.8 15.3C4.2049 14.1219 3.89661 12.8199 3.9 11.5C3.94269 9.9218 4.4106 8.37486 5.25568 7.03248C6.10075 5.6901 7.28815 4.60554 8.7 3.9C9.87812 3.3049 11.1801 2.99661 12.5 3H13C15.0843 3.11499 17.053 3.99476 18.5291 5.47087C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" />
    </svg>
  ),
  Calendar: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  Post: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" />
      <path d="M22 6L12 13L2 6" />
    </svg>
  ),
  User: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Check: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M20 6L9 17L4 12" />
    </svg>
  ),
  Clock: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
};

interface Comment {
  id: number;
  post: number;
  post_title: string;
  post_slug: string;
  author_name: string;
  author_email: string;
  content: string;
  is_approved: boolean;
  created_at: string;
}

const AdminBlogCommentsList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState<Comment[]>([]);
  const [filter, setFilter] = useState('all');
  const [processingId, setProcessingId] = useState<number | null>(null);

  useEffect(() => {
    fetchComments();
  }, []);

  const fetchComments = async () => {
    try {
      const response = await blogAPI.adminGetComments();
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    setProcessingId(id);
    try {
      await blogAPI.adminApproveComment(id);
      await fetchComments();
    } catch (error) {
      console.error('Error approving comment:', error);
      alert('Failed to approve comment');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      setProcessingId(id);
      try {
        await blogAPI.adminDeleteComment(id);
        await fetchComments();
      } catch (error) {
        console.error('Error deleting comment:', error);
        alert('Failed to delete comment');
      } finally {
        setProcessingId(null);
      }
    }
  };

  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const getStatusColor = (isApproved: boolean) => {
    return isApproved ? '#10b981' : '#f59e0b';
  };

  const getStatusBackground = (isApproved: boolean) => {
    return isApproved ? '#d1fae5' : '#fef3c7';
  };

  const filteredComments = filter === 'all' 
    ? comments 
    : filter === 'approved' 
      ? comments.filter(c => c.is_approved)
      : comments.filter(c => !c.is_approved);

  const counts = {
    all: comments.length,
    approved: comments.filter(c => c.is_approved).length,
    pending: comments.filter(c => !c.is_approved).length,
  };

  if (loading) {
    return (
      <div className="abm-loading-container">
        <div className="abm-spinner">
          <Icons.Spinner />
        </div>
        <p className="abm-loading-text">Loading comments...</p>
      </div>
    );
  }

  return (
    <div className="abm-container">
      {/* Header */}
      <div className="abm-header">
        <div className="abm-header-left">
          <h1 className="abm-title">Blog Comments</h1>
          <p className="abm-subtitle">Manage and moderate blog comments</p>
        </div>
        <div className="abm-header-actions">
          <span className="abm-stats-badge">
            <Icons.Clock />
            Pending: {counts.pending}
          </span>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="abm-stats-bar">
        <div className="abm-stat-item">
          <span className="abm-stat-label">Total Comments</span>
          <span className="abm-stat-value">{counts.all}</span>
        </div>
        <div className="abm-stat-divider"></div>
        <div className="abm-stat-item">
          <span className="abm-stat-label">Approved</span>
          <span className="abm-stat-value abm-stat-approved">{counts.approved}</span>
        </div>
        <div className="abm-stat-divider"></div>
        <div className="abm-stat-item">
          <span className="abm-stat-label">Pending</span>
          <span className="abm-stat-value abm-stat-pending">{counts.pending}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="abm-filter-bar">
        <button 
          className={`abm-filter-btn ${filter === 'all' ? 'abm-filter-active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
          <span className="abm-filter-count">{counts.all}</span>
        </button>
        <button 
          className={`abm-filter-btn ${filter === 'approved' ? 'abm-filter-active' : ''}`}
          onClick={() => setFilter('approved')}
        >
          <Icons.Check />
          Approved
          <span className="abm-filter-count">{counts.approved}</span>
        </button>
        <button 
          className={`abm-filter-btn ${filter === 'pending' ? 'abm-filter-active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          <Icons.Clock />
          Pending
          <span className="abm-filter-count">{counts.pending}</span>
        </button>
      </div>

      {/* Comments List */}
      <div className="abm-comments-list">
        {filteredComments.length === 0 ? (
          <div className="abm-empty-state abm-empty-comments">
            <Icons.Empty />
            <p className="abm-empty-text">No comments found</p>
            <p className="abm-empty-subtext">Comments will appear here once readers start engaging</p>
          </div>
        ) : (
          filteredComments.map((comment) => (
            <div key={comment.id} className={`abm-comment-card ${!comment.is_approved ? 'abm-comment-pending' : ''}`}>
              <div className="abm-comment-header">
                <div className="abm-comment-author">
                  <div className="abm-comment-avatar">
                    {getInitials(comment.author_name)}
                  </div>
                  <div className="abm-comment-author-info">
                    <strong>{comment.author_name}</strong>
                    <span className="abm-comment-email">{comment.author_email}</span>
                  </div>
                </div>
                <div className="abm-comment-status">
                  <span 
                    className="abm-status-badge"
                    style={{
                      background: getStatusBackground(comment.is_approved),
                      color: getStatusColor(comment.is_approved)
                    }}
                  >
                    <span className="abm-status-dot" style={{ background: getStatusColor(comment.is_approved) }}></span>
                    {comment.is_approved ? 'Approved' : 'Pending'}
                  </span>
                </div>
              </div>

              <div className="abm-comment-post">
                <Icons.Post />
                On: <a href={`/blog/${comment.post_slug}`} target="_blank" rel="noopener noreferrer">{comment.post_title}</a>
              </div>

              <div className="abm-comment-content">
                {comment.content}
              </div>

              <div className="abm-comment-footer">
                <span className="abm-comment-date">
                  <Icons.Calendar />
                  {new Date(comment.created_at).toLocaleString()}
                </span>
                <div className="abm-comment-actions">
                  {!comment.is_approved && (
                    <button 
                      className="abm-action-btn abm-action-approve"
                      onClick={() => handleApprove(comment.id)}
                      disabled={processingId === comment.id}
                      title="Approve Comment"
                    >
                      {processingId === comment.id ? (
                        <span className="abm-spinner-small"></span>
                      ) : (
                        <>
                          <Icons.Approve />
                          Approve
                        </>
                      )}
                    </button>
                  )}
                  <button 
                    className="abm-action-btn abm-action-delete"
                    onClick={() => handleDelete(comment.id)}
                    disabled={processingId === comment.id}
                    title="Delete Comment"
                  >
                    <Icons.Delete />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminBlogCommentsList;